import { ApolloError, from } from '@apollo/client';
import { InMemoryCache } from '@apollo/client';
import { ApolloClient, split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { createClient as createWsClient } from 'graphql-ws';
import { getApiBaseUrl } from '@oysterjs/corev2/config';
import { getValidationErrors } from './errors';
import { GraphQLFormattedError, Kind, OperationTypeNode } from 'graphql';

export const createClient = (namespace: 'Agency' | 'Clients') =>
  new ApolloClient({
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      },
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      },
      mutate: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only'
      }
    },
    link: from([
      removeTypenameFromVariables(),
      split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === OperationTypeNode.SUBSCRIPTION
          );
        },
        new GraphQLWsLink(
          createWsClient({
            url: `${getApiBaseUrl()}/graphql/query`,
            connectionParams: {
              'X-Oyster-Application-Namespace': namespace
            }
          })
        ),
        createUploadLink({
          uri: `${getApiBaseUrl()}/graphql/query`,
          credentials: 'include',
          fetch: async (uri: string | URL, options: RequestInit) => {
            // Set the app namespace in the headers
            options.headers = {
              ...options.headers,
              'X-Oyster-Application-Namespace': namespace
            };

            // Send the request as usual to the server
            const response = await fetch(uri, options);

            // In most cases, we will want to return the response as usual. However, GraphQL will
            // return a status 422 or 400 if there is a validation error before the resolver is called. In
            // some of these cases, the server is configured to return a JSON response with the
            // validation error. If we detect this case, we actually want to continue with the request
            // as normal, so we need to modify the response's status code to 200.
            if (response.status === 422 || response.status === 400) {
              // Check the body for a validation error
              const cloned = response.clone();
              const body = await cloned.json();

              const graphQLErrors: GraphQLFormattedError[] | undefined = body.errors;

              if (graphQLErrors && !!getValidationErrors(new ApolloError({ graphQLErrors }))) {
                // If there are validation errors, modify the response's status code to 200
                return new Response(JSON.stringify(body), {
                  status: 200,
                  statusText: 'OK',
                  headers: response.headers
                });
              }
            }

            return response;
          }
        })
      )
    ])
  });
